/**
 * @file BackupDestinationSection.tsx
 * @description «Где хранить копии» (phase19.26 шаг 7; шаг 7.1): тип назначения,
 * адрес и доступ, «Оставлять локальную копию», «Проверить подключение».
 *
 * Агент на сервере приложения всегда сначала делает локальную копию, затем
 * синхронизирует её во внешнее хранилище (rclone). Проверку подключения
 * выполняет агент при следующем запуске — панель только ставит запрос.
 *
 * Шаг 7.1: папка локальных копий только для чтения (её задаёт установка, Р1);
 * внешние хранилища видны, но недоступны, пока `externalDestinationsEnabled`
 * выключен (Р4) — их поля, проверка и «Оставлять локальную копию» скрыты.
 * Черновик переживает перечитывание конфига (`useServerDraft`), смена типа
 * очищает адрес, путь и учётные данные (`switchDestinationType`).
 */

import { PlugZap, Save } from "lucide-react";
import { useState } from "react";

import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { EditorialCard } from "../../ui/EditorialCard";
import { Label } from "../../ui/label";
import { useNotify } from "../../ui/NotifyProvider";
import { SelectField } from "../../ui/SelectField";
import {
  backupErrorMessage,
  destinationFieldErrors,
  requestBackupDestinationVerify,
  updateBackupConfig,
  type BackupConfigDto,
  type BackupDestinationType,
  type BackupFieldError,
} from "../../../lib/api-backups";
import {
  DESTINATION_META,
  EXTERNAL_DISABLED_TEXT,
  buildDestinationPayload,
  destinationDraftFrom,
  destinationTypeOptions,
  switchDestinationType,
  type DestinationDraft,
} from "./backup-destination";
import { BackupDestinationErrors } from "./BackupDestinationErrors";
import { BackupDestinationFields } from "./BackupDestinationFields";
import { BackupDiskUsage } from "./BackupDiskUsage";
import { BackupLocalFolder } from "./BackupLocalFolder";
import { BackupSwitchRow } from "./BackupSwitchRow";
import { useServerDraft } from "./use-server-draft";

interface Props {
  config: BackupConfigDto;
  /** Сохранено (`updated` — ответ сервера, подставить сразу) или запрошена проверка — перечитать. */
  onChanged: (updated?: BackupConfigDto) => void;
}

export function BackupDestinationSection({ config, onChanged }: Props) {
  const notify = useNotify();
  const { draft, base, setDraft, replace } = useServerDraft<DestinationDraft>(destinationDraftFrom(config));
  const [busy, setBusy] = useState<"save" | "verify" | null>(null);
  const [fieldErrors, setFieldErrors] = useState<BackupFieldError[]>([]);

  const external = config.externalDestinationsEnabled;
  const patch = (p: Partial<DestinationDraft>) => {
    setDraft((d) => ({ ...d, ...p }));
    setFieldErrors([]);
  };
  const changeType = (next: BackupDestinationType) => {
    setDraft((d) => switchDestinationType(d, next, base));
    setFieldErrors([]);
  };
  const meta = DESTINATION_META[draft.type];
  const isLocal = draft.type === "LOCAL";
  const showExternal = external && !isLocal;
  // LOCAL → LOCAL сохранять нечего: папку задаёт установка.
  const showActions = !isLocal || config.destinationType !== "LOCAL";

  async function save() {
    const built = buildDestinationPayload(draft, config);
    if (!built.ok) {
      notify.error(built.error);
      return;
    }
    setBusy("save");
    try {
      const updated = await updateBackupConfig(built.payload);
      notify.success("Хранилище копий сохранено");
      setFieldErrors([]);
      // Сохранённое больше не правка: черновик и снимок — из ответа сервера.
      replace(destinationDraftFrom(updated));
      onChanged(updated);
    } catch (err) {
      // 400 backup_destination_invalid — список ошибок по полям под карточкой;
      // 422 backup_destination_external_disabled и прочее — понятным тостом.
      const errors = destinationFieldErrors(err);
      if (errors) setFieldErrors(errors);
      else notify.error(backupErrorMessage(err, "Не удалось сохранить хранилище"));
    } finally {
      setBusy(null);
    }
  }

  async function verify() {
    setBusy("verify");
    try {
      await requestBackupDestinationVerify();
      notify.info("Проверка выполнится при следующем запуске бэкапа (или нажмите «Запустить сейчас»).");
      onChanged();
    } catch (err) {
      notify.error(backupErrorMessage(err, "Не удалось запросить проверку"));
    } finally {
      setBusy(null);
    }
  }

  return (
    <EditorialCard>
      <EditorialCard.Header
        title="Где хранить копии"
        subtitle={
          external
            ? "Копия всегда делается на сервере приложения, затем может уходить во внешнее хранилище"
            : "Копии делаются и хранятся на сервере приложения"
        }
      />
      <EditorialCard.Body>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="bk-dst-type">Хранилище</Label>
            <SelectField
              id="bk-dst-type"
              ariaLabel="Хранилище копий"
              value={draft.type}
              onChange={(v) => changeType(v as BackupDestinationType)}
              options={destinationTypeOptions(external)}
              disabled={busy !== null}
            />
            <span className="text-xs text-on-surface-variant">{meta.hint}</span>
            {!external ? <span className="text-xs text-on-surface-variant">{EXTERNAL_DISABLED_TEXT}</span> : null}
          </div>

          {isLocal ? <BackupLocalFolder localRoot={config.localRoot} externalEnabled={external} /> : null}

          {showExternal ? (
            <BackupDestinationFields
              draft={draft}
              onChange={patch}
              saved={draft.type === config.destinationType ? config : null}
              disabled={busy !== null}
            />
          ) : null}

          {/* Диск локальной копии — она делается всегда, и при внешнем хранилище. */}
          <BackupDiskUsage stats={config.diskStats ?? null} warning={config.diskWarning} />

          {showExternal ? (
            <BackupSwitchRow
              label="Оставлять локальную копию на сервере"
              hint="Выключать только при надёжном внешнем хранилище."
              checked={draft.keepLocalCopy}
              onChange={(v) => patch({ keepLocalCopy: v })}
              disabled={busy !== null}
            />
          ) : null}

          {external && config.verifyDestinationRequestedAt ? (
            <Badge variant="secondary" className="self-start">
              Проверка запрошена {new Date(config.verifyDestinationRequestedAt).toLocaleString("ru-RU")}
            </Badge>
          ) : null}

          <BackupDestinationErrors errors={fieldErrors} type={draft.type} />

          {showActions ? (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              {/* Проверяется сохранённое назначение: до сохранения проверять нечего. */}
              {external ? (
                <Button
                  // Проверка подключения — зелёный («проверить»).
                  variant="success"
                  onClick={() => void verify()}
                  loading={busy === "verify"}
                  disabled={busy !== null || config.destinationType === "LOCAL"}
                  title={config.destinationType === "LOCAL" ? "Сначала сохраните внешнее хранилище" : undefined}
                >
                  <PlugZap className="size-4" />
                  {busy === "verify" ? "Запрашиваем…" : "Проверить подключение"}
                </Button>
              ) : null}
              <Button onClick={() => void save()} loading={busy === "save"} disabled={busy !== null}>
                <Save className="size-4" />
                {busy === "save" ? "Сохраняем…" : "Сохранить"}
              </Button>
            </div>
          ) : null}
        </div>
      </EditorialCard.Body>
    </EditorialCard>
  );
}
